import { NotificationTrayView, NotificationView } from '@fullstory/tomato-views/views';
import { Level, NotificationsResponse, ServerNotification, NotificationAction } from '@fullstory/client-api';
import { FragilePopup } from './dialogs';
import { Q, Component } from '@fullstory/tomato';
import { Capture } from './capture';
import { noop, trim } from 'lodash';
import { intl } from '@fullstory/intl';

const NOTIFICATION_MARGIN = 5;

export enum ActionType {
  // A simple anchor.
  Link,

  // Navigate to some history chain.
  HistoryNav,

  // Pop open the Groove widget
  ContactUs
}

type ServerNotificationHandler = (notification: NotificationAction) => Action | null;

export class NotificationsTray extends Component<NotificationTrayView> {
  static AutoHideDelay = 4500;
  static AutoHideDelayShort = 3000;
  static LoadingNotificationId = 'loadingNotification';

  _notifications: Notification[] = []; // Always sorted chronologically. Newest first.

  constructor(container: Q.OfOne = Q.body(), private handleServerNotification: ServerNotificationHandler = () => null) {
    super(new NotificationTrayView());
    container.append(this.view());
  }

  /////////////////////////////////////////////////////////////////
  // Some simple defaults for building notification messages
  // Methods with `Elem` take a Q.OfOne element instead of a string
  /////////////////////////////////////////////////////////////////
  success(msg: string, id?: string, persist: boolean = false) {
    this.successElem(this._textElem(msg), id, persist);
  }

  successLink(msg: string, linkText: string, href: string, id?: string, persist: boolean = false, newTab: boolean = false) {
    const link = Q.createOne("a").setText(linkText).setAttr("href", href);
    if (newTab) {
      link.setAttr("target", "_blank");
    }

    const elem = appendLink(this._textElem(trim(msg)), link);

    this.successElem(elem, id, persist);
  }

  successAction(msg: string, actionText: string, onActionClick: (e: MouseEvent) => void, id?: string, persist: boolean = false) {
    let invoked = false;

    const action = Q.createOne("a").setText(actionText).setAttr('href', '').on('click', e => {
      e.preventDefault();
      if (!invoked) {
        onActionClick(e);
        invoked = true;
      }
    });

    const elem = appendLink(this._textElem(trim(msg)), action);

    this.successElem(elem, id, persist);
  }

  successElem(msg: Q.OfOne, id?: string, persist: boolean = false) {
    const n = this.start(Level.Success).msgElem(msg).lastOneWins(id);
    if (!persist) {
      n.hideAfter(NotificationsTray.AutoHideDelay);
    }
    n.show();
  }

  info(msg: string, id?: string, persist: boolean = false) {
    this.infoElem(this._textElem(msg), id, persist);
  }

  infoElem(msg: Q.OfOne, id?: string, persist: boolean = false) {
    const n = this.start(Level.Info).msgElem(msg).lastOneWins(id);
    if (!persist) {
      n.hideAfter(NotificationsTray.AutoHideDelay);
    }
    n.show();
  }

  warning(msg: string, id?: string, persist: boolean = false) {
    this.warningElem(this._textElem(msg), id, persist);
  }

  warningElem(msg: Q.OfOne, id?: string, persist: boolean = false) {
    const n = this.start(Level.Warning).msgElem(msg).lastOneWins(id);
    if (!persist) {
      n.hideAfter(NotificationsTray.AutoHideDelay);
    }
    n.show();
  }

  error(msg: string, id?: string) {
    this.errorElem(this._textElem(msg), id);
  }

  errorElem(msg: Q.OfOne, id?: string) {
    this.start(Level.Error).msgElem(msg).lastOneWins(id).show();
  }

  errorLink(msg: string, linkText: string, href: string, id?: string) {
    const link = Q.createOne("a").setText(linkText).setAttr("href", href);

    const elem = appendLink(this._textElem(trim(msg)), link);

    this.errorElem(elem, id);
  }

  errorAction(msg: string, linkText: string, href: string, cb: (e: any) => void, id?: string) {
    const link = Q.createOne("a").setText(linkText).setAttr("href", href).on("click", cb);

    const elem = appendLink(this._textElem(trim(msg)), link);

    this.errorElem(elem, id);
  }

  custom(msg: Q.OfOne, id: string): CustomNotification {
    var notif = this.startCustom(id);
    notif.msgElem(msg).show();
    return notif;
  }

  private _textElem(msg: string): Q.OfOne {
    return Q.createOne("span").setText(msg);
  }

  loading() {
    // Don't show a new loading notification if one already exists
    for (let notification of this._notifications) {
      if (notification.id() === NotificationsTray.LoadingNotificationId) {
        return;
      }
    }

    this.start(Level.Loading)
      .msg(intl.formatMessage({ defaultMessage: 'Loading...', id: 'gjBiyj' }))
      .lastOneWins(NotificationsTray.LoadingNotificationId)
      .notDismissable()
      .withSpinner()
      .show();
  }

  // Will dismiss any notification of a specific ID. No-op if no such notifications are up.
  dismissById(id: string) {
    this._findAndDismiss((curr) => {
      return curr.id() == id
    });
  }

  dismiss(n: Notification) {
    this._findAndDismiss((curr) => { return curr == n });
  }

  // Dismiss the loading notification if it's up.
  dismissLoading() {
    this.dismissById(NotificationsTray.LoadingNotificationId);
  }

  private _findAndDismiss(comp: (existing: Notification) => boolean) {
    if (this._notifications.length == 0) { return }

    var found = false;
    for (const [i, notification] of this._notifications.entries()) {
      found = comp(notification);
      if (found) {
        this._notifications.splice(i, 1);
        notification.scheduleRemoval();
        this._layout();
        return
      }
    }
  }

  ////////////////
  // End defaults.
  ////////////////

  serverNotifications(rsp: NotificationsResponse, navigate: (path: string[]) => void, cb: (notification: ServerNotification) => void = noop) {
    if (rsp.Notifications) {
      rsp.Notifications.forEach((notification) => {
        this._serverNotification(notification, navigate, cb);
      });
    }
  }

  _serverNotification(notification: ServerNotification, navigate: (path: string[]) => void, cb: (notification: ServerNotification) => void) {
    var n = this.start(Level[notification.Level]).msg(notification.Msg);
    if (notification.Actions) {
      notification.Actions.forEach((notification) => {
        n.addAction(_actionFromNotification(notification, navigate, this.handleServerNotification));
      });
    }
    if (notification.NotDismissable) {
      n.notDismissable();
    }
    if (notification.Label) {
      n.lastOneWins(notification.Label);
    }
    n.show();
    cb(notification);
  }

  // Use this to build up a custom notification message.
  start(level: Level): BasicNotification {
    var notification = new BasicNotification(level, this);
    notification.appendTo(this.view().notifications);
    return notification;
  }

  startCustom(id: string): CustomNotification {
    var notification = new CustomNotification(this, id);
    notification.appendTo(this.view().notifications);
    return notification;
  }

  hideAll() {
    this._notifications.forEach((n) => {
      n.view().remove();
    });
    this._notifications = [];
    this._layout();
  }

  _addNotification(notification: Notification) {
    var hasId = !!notification.id();
    if (hasId) {
      this.dismissById(notification.id());
    }

    this._notifications.push(notification);
    this._layout();
  }

  private _layout() {
    if (this._notifications.length == 0) {
      this.view().setCss("display", "none");
    } else {
      this.view().setCss("display", "block");
      setTimeout(() => {
        var offset = 0;
        this._notifications.forEach((n) => {
          if (n.removing()) {
            // Don't lay this out!
            return;
          }
          n.view().setCss("opacity", "1");
          n.view().setCss("bottom", offset + "px");
          offset += n.view().offsetHeight() + NOTIFICATION_MARGIN;
        });
      }, 10);
    }
  }
}

function appendLink(elem: Q.OfOne, link: Q.OfOne) {
  // add a space before we append the link
  elem.setText(`${elem.text()} `);
  // append the link
  elem.append(link);
  return elem;
}

interface Notification {
  view(): Q.OfOne;
  id(): string;
  msgElem(msg: Q.OfOne): Notification;
  removing(): boolean;
  scheduleRemoval(): void;
}

// Simple builder for a notification popup. These notifications all follow the same visual pattern and have pre-defined
// colors for different severity levels.
export class BasicNotification extends FragilePopup<NotificationView> implements Notification {
  private _id: string | undefined;
  private _autoHideDelay = -1;
  private _actions: Action[] = [];
  private _isDismissable = true;
  private _removing = false;

  constructor(public level: Level, protected _host: NotificationsTray) {
    super(new NotificationView());
    this.view().setAttr("level", Level[level]);
    if (level < Level.Error) {
      // Anything less than an error gets autohidden (so Success and Info levels).
      this._autoHideDelay = NotificationsTray.AutoHideDelay;
      if (level < Level.Info) {
        this._autoHideDelay = NotificationsTray.AutoHideDelayShort;
      }
    }
    Capture.ignoreClicksOnClasses['notification-dismiss'] = true;
  }

  id(): string {
    return this._id || "";
  }

  removing(): boolean {
    return this._removing;
  }

  msg(msg: string): BasicNotification {
    return this.msgElem(Q.createOne("span").setText(msg));
  }

  msgElem(msg: Q.OfOne): BasicNotification {
    this.view().msg.append(msg);
    return this;
  }

  hideAfter(delay: number): BasicNotification {
    this._autoHideDelay = delay;
    return this;
  }

  addAction(action: Action): BasicNotification {
    this._actions.push(action);
    return this;
  }

  notDismissable(): BasicNotification {
    this._isDismissable = false;
    this.view().dismiss.setCss("display", "none");
    return this;
  }

  lastOneWins(id?: string): BasicNotification {
    this._id = id;
    return this;
  }

  // Show the spinner inside the flag for loading notifications
  withSpinner(): BasicNotification {
    this.view().spinner.setClass("spinner");
    return this;
  }

  // Finalize a notification and queue it to be displayed.
  showImpl() {
    super.showImpl();

    // Add the custom notification actions.
    this._actions.forEach((action) => {
      this._addAction(action);
    });

    // This is the default.
    if (this._isDismissable) {
      this.view().dismiss.on("click", () => this._host.dismiss(this));
    }

    if (this.level == Level.Fatal) {
      this._addAction(Action.ReloadAction);
    }
    this._host._addNotification(this);

    // Autohide if the delay is set.
    if (this._autoHideDelay > 0) {
      window.setTimeout(() => {
        this._host.dismiss(this);
      }, this._autoHideDelay);
    }
  }

  scheduleRemoval() {
    this._removing = true;
    this.view().setCss("opacity", "0");
    if (this._host._notifications.length == 1) {
      this.view().setCss("bottom", "-30px");
    }
    setTimeout(() => {
      this.hide();
    }, 250);
  }

  private _addAction(action: Action) {
    var a = Q.createOne("a").addClass("action").setText(action.name);
    if (action.action) {
      a.on("click", action.action);
    }
    if (action.href) {
      a.setAttr("href", action.href).setAttr("target", "_blank");
    }

    if (this.view().actions.e.children.length > 0) {
      this.view().actions.append(Q.createOne("span").setText(` ${intl.formatMessage({ defaultMessage: 'or', id: 'U5i4iA', description: 'Used to indicate that two or more elements apply (e.g element x or element y)' })} `));
    }

    this.view().actions.append(a);
  }
}

// This allows us to create custom notifications that contain any information that we wish to show to users.
export class CustomNotification extends FragilePopup<Q.OfOne> implements Notification {
  private _removing = false;

  constructor(private _host: NotificationsTray, private _id: string) {
    super(Q.createOne("div").addClass("customNotification"));
  }

  id(): string {
    return this._id;
  }

  removing(): boolean {
    return this._removing;
  }

  msgElem(msg: Q.OfOne): CustomNotification {
    this.view().append(msg);
    return this;
  }

  dismiss() {
    this._host.dismiss(this);
  }

  // Finalize a notification and queue it to be displayed.
  showImpl() {
    super.showImpl();
    this._host._addNotification(this);
  }

  scheduleRemoval() {
    this._removing = true;
    this.view().setCss("opacity", "0");
    if (this._host._notifications.length == 1) {
      this.view().setCss("bottom", "-30px");
    }
    setTimeout(() => {
      this.hide();
    }, 250);
  }

  hideFromCapture(): boolean {
    return false;
  }
}

export class Action {
  static ReloadAction = new Action(intl.formatMessage({ defaultMessage: 'Reload', id: 'fdCS5/' }), () => { window.location.reload() });
  static StatusPageAction = new Action(intl.formatMessage({ defaultMessage: 'View status page', id: 'ULsNX1' }), () => { }, "https://fullstory.statuspage.io/");

  constructor(public name: string, public action: () => void, public href?: string) { }
}

function _actionFromNotification(notification: NotificationAction, navigate: (path: string[]) => void, handler: ServerNotificationHandler): Action {
  const actionFromHandler = handler(notification);

  if (actionFromHandler != null) return actionFromHandler;

  switch (notification.Type) {
    case ActionType[ActionType.Link]:
      return new Action(notification.Name, () => { }, notification.Href);
    case ActionType[ActionType.HistoryNav]:
      return new Action(notification.Name, () => {
        navigate(notification.History);
      });
    default:
      throw "Unsupported Notification Action Type " + notification.Type
  }
}
